import { Button } from "@mui/material";
import React, { useState, ChangeEvent } from "react";
import Spinner from "../components/Spinner";
import config from "../Config";
import { useSounds } from "../hooks/UseSounds";

function validate(
  event: ChangeEvent,
  { sound, func }: { sound: string; func: Function }
) {
  const btn: HTMLButtonElement = document.getElementById(
    "delete-button"
  ) as HTMLButtonElement;
  btn.disabled = (event.target as HTMLInputElement).value !== sound;
  if (!btn.disabled) {
    btn.onclick = () => func(sound);
  } else {
    btn.onclick = null;
  }
}

function DeleteButtons({
  clips,
  setSelectedSound,
}: {
  clips: string[];
  setSelectedSound: React.Dispatch<
    React.SetStateAction<string | null | undefined>
  >;
}) {
  const alphaGroups = clips?.reduce(
    (acc: Map<string, string[]>, item: string) => {
      const firstLetter: string = item.trim()[0];

      if (!acc.get(firstLetter)) {
        acc.set(firstLetter, []);
      }
      acc.get(firstLetter)?.push(item);

      return acc;
    },
    new Map<string, string[]>()
  );
  const buttonWidth = Math.max(...clips.map((i) => i.length), 17) + 1;

  const SoundButton = ({ soundName }: { soundName: string }) => (
    <Button
      type="button"
      sx={{
        width: `${buttonWidth}ch`,
        color: "white",
        border: "1px solid black",
        borderRadius: "12px",
        backgroundColor: "rgb(74, 74, 74)",
        height: "4em",
        textTransform: "none",
        margin: "2px",
        "&:hover": {
          border: "2px solid darkred",
          backgroundColor: "rgb(74, 74, 74)",
        },
      }}
      fullWidth
      id={soundName}
      key={soundName}
      variant="outlined"
      size="medium"
      onClick={() => setSelectedSound(soundName)}
    >
      {soundName}
    </Button>
  );

  return (
    <div id="buttons-div">
      <p>{clips?.length} items</p>
      {Array.from(alphaGroups.keys()).map((key) => {
        const list: string[] = alphaGroups.get(key) ?? [];
        if (!list || list.length < 0) {
          return null;
        }
        return (
          <>
            <p key={key}>{key.toUpperCase()}</p>
            <hr />
            <p key={`${key}-delete-wrap`}>
              {list.map((soundName) =>
                SoundButton({
                  soundName,
                })
              )}
            </p>
          </>
        );
      })}
    </div>
  );
}

function DeleteCheck({ sound, func }: { sound: string; func: Function }) {
  return (
    <>
      Are you sure you want to delete that?
      <br />
      <small>
        Type out &quot;
        {sound}
        &quot; in the box below and then select delete to continue.
      </small>
      <br />
      <input
        size={50}
        id="search"
        type="text"
        autoComplete="off"
        onChange={(event) => validate(event, { sound, func })}
        data-testid="delete-input"
      />
      <br />
      <button
        type="button"
        id="delete-button"
        onClick={() => func(sound)}
        disabled
      >
        DELETE
      </button>
    </>
  );
}

export default function Delete() {
  const [clips, setClips] = useState<string[]>([]);
  const [selectedSound, setSelectedSound] = useState<string | null>();

  const deleteSound = (sound: string) => {
    fetch(`${config.clipCrudUrl}${sound}`, {
      method: "DELETE",
    });
    setSelectedSound(null);
  };

  useSounds(setClips);

  if (selectedSound) {
    return <DeleteCheck sound={selectedSound} func={deleteSound} />;
  }

  return clips.length ? (
    <DeleteButtons clips={clips} setSelectedSound={setSelectedSound} />
  ) : (
    <Spinner />
  );
}
